import JobsBackOfficeEditCandidateFormUpdateJobsModeratorsSubmittedHiringMutation from '@/components/partials/jobs/JobsBackOffice/JobsBackOfficeEditCandidateForm/apollo/JobsBackOfficeEditCandidateFormUpdateJobsModeratorsSubmittedHiring.mutation.gql';
import type { ActionTree, GetterTree, MutationTree } from 'vuex';
import HiringFragment from '~/components/partials/jobs/hirings/apollo/HiringFragment.gql';
import SubmitCandidateMutation from '~/components/partials/jobs/JobsBackOffice/JobsBackOfficeEditCandidateForm/apollo/SubmitCandidate.gql';
import { readableClients } from '~/components/partials/jobs/JobsBackOffice/readables';
import type {
  CandidateAttributes,
  CandidateAttributesVisability,
  HiringFragment as HiringFragmentType,
  JobsFlexRole,
  JobsHiringBadge,
  JobsModeratorsCandidate,
  MutationUpdateJobsModeratorsSubmittedHiringArgs,
  SubmitCandidateMutation as SubmitCandidateMutationType,
  SubmitCandidateMutationVariables,
} from '~/types/graphql';
import { JobsModeratorsHiringEventName } from '~/types/graphql';
import type { RootState } from '~/types/store';
import type {
  JobsBackOfficeEditCandidateForm,
  JobsCandidateEditState,
  SetFormFieldMutationArgs,
  SubmissionState,
  SubmitCandidateToCompaniesActionArgs,
  ToggleFieldVisibilityMutationArgs,
  UpdateCandidateCardActionArgs,
  VisibilityConfig,
} from '~/types/store/jobs-candidate-edit';

const revertToNullIfNumberableFieldIsEmpty = (
  formField: number | string,
  initialField: null | string | number | undefined,
) => {
  return !Number(formField) && initialField === null ? null : formField;
};

const fieldsWithoutVisibilityConfig: Array<keyof JobsBackOfficeEditCandidateForm> = ['badges'];

export const state = (): JobsCandidateEditState => ({
  form: {
    linkedInUrl: '',
    badges: [],
    headline1: '',
    headline2: '',
    headline3: '',
    salary: 0,
    ote: 0,
    attainment: 0,
    dealSize: '',
    dealCycle: '',
    quota: 0,
    clients: '',
    bullet1: '',
    bullet2: '',
    teamRanking: '',
    whatIWant: '',
    leadSources: '',
    workHistories: [],
    departments: '',
    csmAccountsNumber: '',
    csmQuotaMetric: '',
    csmQuotaType: '',
    csmRenewalValue: '',
    csmTotalBusinessValue: '',
    csmUpsellValue: '',
    quotaAttainmentByTeamPerPeriod: '',
    vpDepartmentSize: '',
    vpManagedRoles: '',
    vpQuotaType: '',
    flexRoles: [],
    flexBriefVideoUrl: '',
    flexRating: '',
    flexAppliedCriteria: [],
    flexBandwidth: '',
    flexReasonForParticipation: '',
    flexPreviousExperience: '',
    flexUnderstandingOfSales: '',
  },
  visibilityConfig: {
    linkedInUrl: false,
    badges: false,
    headline1: false,
    headline2: false,
    headline3: false,
    salary: false,
    ote: false,
    attainment: false,
    dealSize: false,
    dealCycle: false,
    quota: false,
    clients: false,
    bullet1: false,
    bullet2: false,
    teamRanking: false,
    whatIWant: false,
    leadSources: false,
    workHistories: false,
    departments: false,
    csmAccountsNumber: false,
    csmQuotaMetric: false,
    csmQuotaType: false,
    csmRenewalValue: false,
    csmTotalBusinessValue: false,
    csmUpsellValue: false,
    quotaAttainmentByTeamPerPeriod: false,
    vpDepartmentSize: false,
    vpManagedRoles: false,
    vpQuotaType: true,
    flexRoles: false,
    flexBriefVideoUrl: false,
    flexRating: false,
    flexAppliedCriteria: false,
    flexBandwidth: false,
    flexReasonForParticipation: false,
    flexPreviousExperience: false,
    flexUnderstandingOfSales: false,
  },
  submissionState: 'idle',
});

export const mutations: MutationTree<JobsCandidateEditState> = {
  setFormField(state, { fieldName, value }: SetFormFieldMutationArgs): void {
    Object.assign(state.form, { [fieldName]: value });
  },
  toggleFieldVisibility(state, { fieldName, isSelected }: ToggleFieldVisibilityMutationArgs) {
    if (fieldsWithoutVisibilityConfig.includes(fieldName)) {
      return;
    }
    state.visibilityConfig[fieldName] = isSelected;
  },
  updateCheckboxGroup(
    state,
    {
      fieldName,
      value,
      isSelected,
    }: {
      fieldName: keyof JobsBackOfficeEditCandidateForm;
      value: JobsFlexRole | JobsHiringBadge;
      isSelected: boolean;
    },
  ): void {
    if (!Array.isArray(state.form[fieldName])) return;
    const prevValue = state.form[fieldName] as Array<JobsFlexRole | JobsHiringBadge>;
    if (prevValue.includes(value) && !isSelected) {
      // @ts-ignore
      state.form[fieldName] = prevValue.filter(b => b !== value);
      return;
    }
    // @ts-ignore
    state.form[fieldName].push(value);
  },
  setSubmissionState(state, submissionState: SubmissionState): void {
    state.submissionState = submissionState;
  },
  setVisibilityConfig(state, config: VisibilityConfig): void {
    state.visibilityConfig = { ...config };
  },
  resetFormValues(
    state,
    { defaultValues, isUpdate }: { defaultValues: JobsModeratorsCandidate; isUpdate: boolean },
  ): void {
    const linkedInUrl = defaultValues.linkedinUrl ?? '';
    const badges = isUpdate ? [...(defaultValues.badges ?? [])] : [];
    const flexRoles = isUpdate ? [...(defaultValues.flexRoles ?? [])] : [];
    const flexAppliedCriteria = isUpdate ? [...(defaultValues.flexAppliedCriteria ?? [])] : [];
    const headline1 = isUpdate ? defaultValues.headline1 ?? '' : '';
    const headline2 = isUpdate ? defaultValues.headline2 ?? '' : '';
    const headline3 = isUpdate ? defaultValues.headline3 ?? '' : '';
    const bullet1 = isUpdate ? defaultValues.bullet1 ?? '' : '';
    const bullet2 = isUpdate ? defaultValues.bullet2 ?? '' : '';
    const salary = Number(defaultValues.baseSalary) ?? null;
    const ote = Number(defaultValues.ote) ?? null;
    const attainment = Number(defaultValues.quotaAttainmentPerLastYear) ?? null;
    const dealSize = defaultValues.dealSize ?? '';
    const dealCycle = defaultValues.salesCycleLength ?? '';
    const quota = Number(defaultValues.annualQuota) ?? null;
    const teamRanking = defaultValues.teamRanking ?? '';
    const whatIWant = defaultValues.idealJobDescription ?? '';
    const leadSources = defaultValues.leadSources ?? '';
    const csmAccountsNumber = defaultValues.csmAccountsNumber ?? '';
    const csmQuotaMetric = defaultValues.csmQuotaMetric ?? '';
    const csmQuotaType = defaultValues.csmQuotaType ?? '';
    const csmRenewalValue = defaultValues.csmRenewalValue ?? '';
    const csmTotalBusinessValue = defaultValues.csmTotalBusinessValue ?? '';
    const csmUpsellValue = defaultValues.csmUpsellValue ?? '';
    const clients = readableClients(defaultValues.clients ?? []);
    const quotaAttainmentByTeamPerPeriod = defaultValues.quotaAttainmentByTeamPerPeriod ?? '';
    const vpDepartmentSize = defaultValues.vpDepartmentSize ?? '';
    const vpManagedRoles = defaultValues.vpManagedRoles ?? '';

    state.form = {
      ...state.form,
      linkedInUrl,
      badges,
      flexRoles,
      flexAppliedCriteria,
      headline1,
      headline2,
      headline3,
      bullet1,
      bullet2,
      salary,
      ote,
      attainment,
      dealSize,
      dealCycle,
      quota,
      teamRanking,
      whatIWant,
      leadSources,
      csmAccountsNumber,
      csmQuotaMetric,
      csmQuotaType,
      csmRenewalValue,
      csmTotalBusinessValue,
      csmUpsellValue,
      clients,
      quotaAttainmentByTeamPerPeriod,
      vpDepartmentSize,
      vpManagedRoles,
    };
  },
  setFormValues(state, { form }: { form: JobsBackOfficeEditCandidateForm }): void {
    state.form = {
      ...state.form,
      ...form,
    };
  },
};

export const getters: GetterTree<JobsCandidateEditState, RootState> = {
  headlines(state): Array<string> {
    const headlines: string[] = [];
    const { headline1, headline2, headline3 } = state.form;

    const {
      headline1: isHeadline1Visible,
      headline2: isHeadline2Visible,
      headline3: isHeadline3Visible,
    } = state.visibilityConfig;

    if (isHeadline1Visible && headline1) headlines.push(headline1);
    if (isHeadline2Visible && headline2) headlines.push(headline2);
    if (isHeadline3Visible && headline3) headlines.push(headline3);

    return headlines;
  },
  candidateAttributes(state): CandidateAttributes {
    return {
      annualQuota: String(state.form.quota),
      baseSalary: String(state.form.salary),
      dealSize: state.form.dealSize,
      linkedinUrl: state.form.linkedInUrl,
      ote: String(state.form.ote),
      quotaAttainmentPerPeriod: String(state.form.attainment),
      salesCycleLength: state.form.dealCycle,
      badges: state.form.badges,
      headline1: state.form.headline1,
      headline2: state.form.headline2,
      headline3: state.form.headline3,
      bullet1: state.form.bullet1,
      bullet2: state.form.bullet2,
      teamRanking: state.form.teamRanking,
      leadSources: state.form.leadSources,
      idealJobDescription: state.form.whatIWant,
      csmAccountsNumber: state.form.csmAccountsNumber,
      csmQuotaMetric: state.form.csmQuotaMetric,
      csmQuotaType: state.form.csmQuotaType,
      csmRenewalValue: state.form.csmRenewalValue,
      csmTotalBusinessValue: state.form.csmTotalBusinessValue,
      csmUpsellValue: state.form.csmUpsellValue,
      quotaAttainmentByTeamPerPeriod: state.form.quotaAttainmentByTeamPerPeriod,
      vpDepartmentSize: state.form.vpDepartmentSize,
      vpManagedRoles: state.form.vpManagedRoles,
      vpQuotaType: state.form.vpQuotaType,
      flexRoles: state.form.flexRoles,
      flexBriefVideoUrl: state.form.flexBriefVideoUrl,
      flexRating: state.form.flexRating.length ? Number(state.form.flexRating) : null,
      flexAppliedCriteria: state.form.flexAppliedCriteria,
      flexBandwidth: state.form.flexBandwidth,
      flexReasonForParticipation: state.form.flexReasonForParticipation,
      flexPreviousExperience: state.form.flexPreviousExperience,
      flexUnderstandingOfSales: state.form.flexUnderstandingOfSales,
    };
  },
  candidateAttributesVisability(state): CandidateAttributesVisability {
    return {
      isAnnualQuotaVisible: state.visibilityConfig.quota,
      isBaseSalaryVisible: state.visibilityConfig.salary,
      isClientsVisible: state.visibilityConfig.clients,
      isDealSizeVisible: state.visibilityConfig.dealSize,
      isLinkedinUrlVisible: state.visibilityConfig.linkedInUrl,
      isOteVisible: state.visibilityConfig.ote,
      isQuotaAttainmentPerPeriodVisible: state.visibilityConfig.attainment,
      isSalesCycleLengthVisible: state.visibilityConfig.dealCycle,
      isHeadline1Visible: state.visibilityConfig.headline1,
      isHeadline2Visible: state.visibilityConfig.headline2,
      isHeadline3Visible: state.visibilityConfig.headline3,
      isBullet1Visible: state.visibilityConfig.bullet1,
      isBullet2Visible: state.visibilityConfig.bullet2,
      isTeamRankingVisible: state.visibilityConfig.teamRanking,
      isLeadSourcesVisible: state.visibilityConfig.leadSources,
      isIdealJobDescriptionVisible: state.visibilityConfig.whatIWant,
      isWorkHistoriesVisible: state.visibilityConfig.workHistories,
      isDepartmentsVisible: state.visibilityConfig.departments,
      isCsmAccountsNumberVisible: state.visibilityConfig.csmAccountsNumber,
      isCsmQuotaMetricVisible: state.visibilityConfig.csmQuotaMetric,
      isCsmQuotaTypeVisible: state.visibilityConfig.csmQuotaType,
      isCsmRenewalValueVisible: state.visibilityConfig.csmRenewalValue,
      isCsmTotalBusinessValueVisible: state.visibilityConfig.csmTotalBusinessValue,
      isCsmUpsellValueVisible: state.visibilityConfig.csmUpsellValue,
      isQuotaAttainmentByTeamPerPeriodVisible: state.visibilityConfig.quotaAttainmentByTeamPerPeriod,
      isVpDepartmentSizeVisible: state.visibilityConfig.vpDepartmentSize,
      isVpManagedRolesVisible: state.visibilityConfig.vpManagedRoles,
      isVpQuotaTypeVisible: state.visibilityConfig.vpQuotaType,
      isFlexRolesVisible: state.visibilityConfig.flexRoles,
      isFlexBriefVideoUrlVisible: state.visibilityConfig.flexBriefVideoUrl,
      isFlexRatingVisible: state.visibilityConfig.flexRating,
      isFlexAppliedCriteriaVisible: state.visibilityConfig.flexAppliedCriteria,
      isFlexBandwidthVisible: state.visibilityConfig.flexBandwidth,
      isFlexReasonForParticipationVisible: state.visibilityConfig.flexReasonForParticipation,
      isFlexPreviousExperienceVisible: state.visibilityConfig.flexPreviousExperience,
      isFlexUnderstandingOfSalesVisible: state.visibilityConfig.flexUnderstandingOfSales,
    };
  },
};

export const actions: ActionTree<JobsCandidateEditState, RootState> = {
  async submitCandidateToCompanies({ getters }, { selectedHiringIds }: SubmitCandidateToCompaniesActionArgs) {
    if (this.app.apolloProvider) {
      await this.app.apolloProvider.defaultClient.mutate<SubmitCandidateMutationType, SubmitCandidateMutationVariables>(
        {
          mutation: SubmitCandidateMutation,
          variables: {
            input: {
              hiringIds: selectedHiringIds,
              candidateAttributes: getters.candidateAttributes,
              candidateAttributesVisability: getters.candidateAttributesVisability,
            },
          },
          update(store, result) {
            result.data?.jobsModeratorsSubmitHirings?.forEach(hiring => {
              const hiringId = `JobsModeratorsHiring:${hiring.id}`;
              const hiringFragment = store.readFragment<HiringFragmentType>({ id: hiringId, fragment: HiringFragment });
              if (hiringFragment) {
                store.writeFragment({
                  id: hiringId,
                  fragment: HiringFragment,
                  data: {
                    ...hiringFragment,
                    state: hiring.state,
                    secondsInState: 1,
                    // refactor: looks like we should get new available events from be here
                    availableEvents: hiringFragment.availableEvents.filter(
                      e => e !== JobsModeratorsHiringEventName.Submit,
                    ),
                  },
                });
              }
            });
          },
        },
      );
    }
  },
  async updateCandidateCard({ getters }, { hiringId, candidate }: UpdateCandidateCardActionArgs) {
    if (!this.app.apolloProvider) throw new Error('Apollo client is not initialized');

    const apolloClient = this.app.apolloProvider.defaultClient;

    const ote = revertToNullIfNumberableFieldIsEmpty(getters.candidateAttributes.ote, candidate.ote);
    const baseSalary = revertToNullIfNumberableFieldIsEmpty(
      getters.candidateAttributes.baseSalary,
      candidate.baseSalary,
    );
    const quotaAttainmentPerPeriod = revertToNullIfNumberableFieldIsEmpty(
      getters.candidateAttributes.quotaAttainmentPerPeriod,
      candidate.quotaAttainmentPerPeriod,
    );
    const annualQuota = revertToNullIfNumberableFieldIsEmpty(
      getters.candidateAttributes.annualQuota,
      candidate.annualQuota,
    );

    const candidateAttributes = {
      ...getters.candidateAttributes,
      ote,
      baseSalary,
      quotaAttainmentPerPeriod,
      annualQuota,
    };

    await apolloClient.mutate<{}, MutationUpdateJobsModeratorsSubmittedHiringArgs>({
      mutation: JobsBackOfficeEditCandidateFormUpdateJobsModeratorsSubmittedHiringMutation,
      variables: {
        input: {
          hiringId,
          candidateAttributes,
          candidateAttributesVisability: getters.candidateAttributesVisability,
        },
      },
    });
  },
};
